import React from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { Authenticated, logoutRequest } from '@ackee/petrus';
import { FlowType } from '@ackee/petrus';
import { useDispatch, useSelector } from 'react-redux';
import { Spin } from 'antd';

import config from 'config';
import { Icon, IconType } from 'modules/ui';
import { useLogoutMessage } from 'modules/auth';
import { Amplify } from 'modules/amplify';
import { useAnalytics } from 'modules/analytics';

import { NewElevatorDialog, NewElevatorDialogProvider } from 'modules/elevator-new';

import Layout from './Layout';
import Routes from './Routes';
import AuthRoutes from './AuthRoutes';
import { NewIncidentDialog, GlobalDialogProvider } from 'modules/incident-new';
import SpecialRoutes from './SpecialRoutes/SpecialRoutes';
import { useLocation } from 'react-router-dom';
import { SupportDialog } from 'modules/support/components/SupportDialog';
import { ValidateConfirmDialog } from 'modules/incidents/components/ValidateConfirmDialog';
import { EditIncidentDialog } from 'modules/incidents/components/EditIncidentDialog';
import TeamDialog from 'modules/teams/components/TeamDialog/TeamDialog';
import useUserPermissions from 'modules/auth/hooks/useUserPermissions';
import { GuestAuthProvider, useGuestAuth, GuestApp, GuestVerifyPage, isGuestMagicLink } from 'modules/guest';
import SalesforceSessionBootstrap from 'modules/auth/components/SalesforceSessionBootstrap';
import { SALESFORCE_ACCESS_TOKEN_STORAGE_KEY } from 'modules/auth/constantsSalesforce';
import { authFlowTypeSelector } from 'modules/auth/services/selectors';

interface GetNavItems {
  intl: IntlShape;
  hasViewElevatorsPermission: boolean;
  hasViewDocumentsPermission: boolean;
  shouldUploadPriceCheckDocumentsPermissions: boolean;
  shouldUploadDeficiencyCheckPermissions: boolean;
  shouldUploadQuotationCheckPermissions: boolean;
  shouldUploadContractCheckPermissions: boolean;
  shouldViewMaintenanceOptimizationPermissions: boolean;
  shouldViewOperatorObligationsPermissions: boolean;
  shouldViewSupportRequestPermissions: boolean;
  shouldViewTeamsPermissions: boolean;
  shouldViewWorkOrders: boolean;
  isAdmin: boolean;
  hasViewDocumentsExtractionPermission: boolean;
}

const getNavItems = ({
  intl,
  hasViewElevatorsPermission,
  hasViewDocumentsPermission,
  shouldUploadPriceCheckDocumentsPermissions,
  shouldUploadDeficiencyCheckPermissions,
  shouldUploadQuotationCheckPermissions,
  shouldUploadContractCheckPermissions,
  shouldViewMaintenanceOptimizationPermissions,
  shouldViewOperatorObligationsPermissions,
  shouldViewSupportRequestPermissions,
  shouldViewTeamsPermissions,
  shouldViewWorkOrders,
  isAdmin,
  hasViewDocumentsExtractionPermission,
}: GetNavItems) => {
  const navItems = [
    {
      name: intl.formatMessage({ id: 'page.home.title' }),
      path: config.routes.home,
      icon: <Icon type={IconType.HOME} />,
      key: config.routes.home,
      shouldShow: true,
    },
    {
      name: intl.formatMessage({ id: 'page.elevators.title' }),
      path: config.routes.elevators,
      icon: <Icon type={IconType.ELEVATOR} />,
      key: config.routes.elevators,
      shouldShow: hasViewElevatorsPermission,
    },
    // {
    //     name: intl.formatMessage({ id: 'page.iot_overview.title' }),
    //     path: '/iot_overview',
    //     icon: <Icon type={Icon.Types.IOT} />,
    //     key: '/iot_overview',
    // },
    // {
    //     name: intl.formatMessage({ id: 'page.reports.title' }),
    //     path: '/reports',
    //     icon: <Icon type={Icon.Types.REPORT} />,
    //     key: '/reports',
    // },
    {
      name: intl.formatMessage({ id: 'page.documents.title' }),
      path: config.routes.documents,
      icon: <Icon type={IconType.DOCUMENT} />,
      key: config.routes.documents,
      shouldShow: hasViewDocumentsPermission,
    },
    {
      name: intl.formatMessage({ id: 'page.documentProcessing.title' }),
      path: config.routes.documentProcessing,
      icon: <Icon type={IconType.DOCUMENT} />,
      key: config.routes.documentProcessing,
      shouldShow: hasViewDocumentsExtractionPermission,
    },
    {
      name: intl.formatMessage({ id: 'page.reporting.title' }),
      path: config.routes.reporting,
      icon: <Icon type={IconType.DOCUMENT} />,
      key: config.routes.reporting,
      shouldShow: hasViewDocumentsExtractionPermission,
    },
    {
      name: intl.formatMessage({ id: 'page.assistant.title' }),
      path: config.routes.assistant,
      icon: <Icon type={IconType.SEARCH} />,
      key: config.routes.assistant,
      shouldShow: hasViewDocumentsExtractionPermission,
    },
    // {
    //     name: intl.formatMessage({ id: 'page.contracts.title' }),
    //     path: '/contracts',
    //     icon: <Icon type={Icon.Types.CONTRACT} />,
    //     key: '/contracts',
    // },
    // {
    //     name: intl.formatMessage({ id: 'page.financials.title' }),
    //     path: '/financials',
    //     icon: <Icon type={Icon.Types.FINANCIALS} />,
    //     key: '/financials',
    // },
    // {
    //     name: intl.formatMessage({ id: 'page.operator_obligations.title' }),
    //     path: '/obligations',
    //     icon: <Icon type={Icon.Types.SHIELD} />,
    //     key: '/obligations',
    // },
    // {
    //     name: intl.formatMessage({ id: 'page.tasks.title' }),
    //     path: '/tasks',
    //     icon: <Icon type={Icon.Types.TASKS} />,
    //     key: '/tasks',
    // },

    {
      name: intl.formatMessage({ id: 'page.uploadZUES.menuTitle' }),
      path: config.routes.uploadZUES,
      icon: <Icon type={IconType.ZUES_PRICE_CHECK} />,
      key: config.routes.uploadZUES,
      shouldShow: shouldUploadPriceCheckDocumentsPermissions,
    },
    {
      name: intl.formatMessage({ id: 'page.uploadZUESDeficiency.menuTitle' }),
      path: config.routes.uploadZUESDeficiency,
      icon: <Icon type={IconType.ZUES_DEFICIENCY_CHECK} />,
      key: config.routes.uploadZUESDeficiency,
      shouldShow: shouldUploadDeficiencyCheckPermissions,
    },
    {
      name: intl.formatMessage({ id: 'page.uploadQuotation.menuTitle' }),
      path: config.routes.uploadQuotation,
      icon: <Icon type={IconType.QUOTATION_CHECK} />,
      key: config.routes.uploadQuotation,
      shouldShow: shouldUploadQuotationCheckPermissions,
    },
    {
      name: intl.formatMessage({ id: 'page.uploadContract.menuTitle' }),
      path: config.routes.uploadContract,
      icon: <Icon type={IconType.CONTRACT_CHECK} />,
      key: config.routes.uploadContract,
      shouldShow: shouldUploadContractCheckPermissions,
    },
    {
      name: intl.formatMessage({ id: 'page.fakePages.maintenanceAnalysis.menuTitle' }),
      path: config.routes.maintenanceAnalysis,
      icon: <Icon type={IconType.MAINTENANCE_ANALYSIS} />,
      key: config.routes.maintenanceAnalysis,
      shouldShow: shouldViewMaintenanceOptimizationPermissions,
    },
    {
      name: intl.formatMessage({ id: 'page.fakePages.operatorObligations.menuTitle' }),
      path: config.routes.operatorObligations,
      icon: <Icon type={IconType.OPERATOR_OBLIGATION} />,
      key: config.routes.operatorObligations,
      shouldShow: shouldViewOperatorObligationsPermissions,
    },
    {
      name: intl.formatMessage({ id: 'page.support.title' }),
      path: '/support',
      icon: <Icon type={IconType.SUPPORT} />,
      key: '/support',
      shouldShow: shouldViewSupportRequestPermissions,
    },
    {
      name: intl.formatMessage({ id: 'page.workorders.title' }),
      path: '/work-orders',
      icon: <Icon type={IconType.TASKS} />,
      key: '/work-orders',
      shouldShow: shouldViewWorkOrders || isAdmin,
    },
    {
      name: intl.formatMessage({ id: 'page.teams.title' }),
      path: '/teams',
      icon: <Icon type={IconType.SETTINGS} />,
      key: '/teams',
      shouldShow: shouldViewTeamsPermissions,
    },
  ];

  return navItems.filter(({ shouldShow }) => shouldShow);
};
const getItemsOutsideLayout = () => [config.routes.browserIssues];

const AppInner = () => {
  const intl = useIntl();
  const location = useLocation();
  const isAdmin = location.search.includes('elevoniq-modifi');
  const { token: guestToken } = useGuestAuth();
  const flowType = useSelector(authFlowTypeSelector);
  const dispatch = useDispatch();
  const shouldResetAuthenticatedLogin =
    flowType === FlowType.AUTHENTICATED &&
    location.pathname === config.routes.login &&
    new URLSearchParams(location.search).has('redirectUrl');
  useAnalytics();
  useLogoutMessage();

  useEffect(() => {
    if (shouldResetAuthenticatedLogin) {
      dispatch(logoutRequest());
    }
  }, [dispatch, shouldResetAuthenticatedLogin]);

  const {
    hasViewElevatorsPermission,
    hasViewDocumentsPermission,
    shouldUploadPriceCheckDocumentsPermissions,
    shouldUploadDeficiencyCheckPermissions,
    shouldUploadQuotationCheckPermissions,
    shouldUploadContractCheckPermissions,
    shouldViewMaintenanceOptimizationPermissions,
    shouldViewOperatorObligationsPermissions,
    shouldViewSupportRequestPermissions,
    shouldViewTeamsPermissions,
    shouldViewWorkOrders,
    hasViewDocumentsExtractionPermission,
  } = useUserPermissions();

  if (isGuestMagicLink(location.pathname, location.search)) {
    return <GuestVerifyPage />;
  }

  if (guestToken) {
    return <GuestApp />;
  }

  if (shouldResetAuthenticatedLogin) {
    return null;
  }

  const hasSalesforceToken = (() => {
    try {
      return Boolean(localStorage.getItem(SALESFORCE_ACCESS_TOKEN_STORAGE_KEY));
    } catch {
      return false;
    }
  })();

  const shouldShowAuthRestoringLoader =
    hasSalesforceToken && (flowType === FlowType.INDETERMINATE || flowType === FlowType.ANONYMOUS);

  const AuthRestoringLoader = () => (
    <div style={{ width: '100vw', height: '100vh', display: 'grid', placeItems: 'center' }}>
      <Spin size="large" />
    </div>
  );

  return (
      <Amplify>
          <SalesforceSessionBootstrap />
          <Authenticated FallbackComponent={shouldShowAuthRestoringLoader ? AuthRestoringLoader : Routes}>
              <NewElevatorDialogProvider>
                  <GlobalDialogProvider>
                      <SpecialRoutes />
                      {!getItemsOutsideLayout().includes(location.pathname) && (
                      <Layout
                          menuItems={getNavItems({
                  intl,
                  hasViewElevatorsPermission,
                  hasViewDocumentsPermission,
                  shouldUploadPriceCheckDocumentsPermissions,
                  shouldUploadDeficiencyCheckPermissions,
                  shouldUploadQuotationCheckPermissions,
                  shouldUploadContractCheckPermissions,
                  shouldViewMaintenanceOptimizationPermissions,
                  shouldViewOperatorObligationsPermissions,
                  shouldViewSupportRequestPermissions,
                  shouldViewTeamsPermissions,
                  shouldViewWorkOrders,
                  isAdmin,
                  hasViewDocumentsExtractionPermission,
                })}
              >
                          <AuthRoutes />
                      </Layout>
            )}
                      <NewElevatorDialog />
                      <NewIncidentDialog />
                      <TeamDialog />
                      <SupportDialog />
                      <ValidateConfirmDialog />
                      <EditIncidentDialog />
                  </GlobalDialogProvider>
              </NewElevatorDialogProvider>
          </Authenticated>
      </Amplify>
  );
};

const App = () => (
    <GuestAuthProvider>
        <AppInner />
    </GuestAuthProvider>
);

export default App;
